import type { Sku, SkuPageContent } from '@/types/interfaces/sku'
import type {
    SkuRefreshPageConfig,
    SkuComponentConfig,
    SkuComponentConfigMap,
    SkuComponentType,
} from '@/types/enums/sku-component-types'
import {
    getSkuPageTrustpilotVariant,
    showsProductPageTrustpilot,
} from '@/lib/hardcoded-data/CategoryPages/testimonials'

// TODO: Replace with actual placeholder image
const PLACEHOLDER_IMG = 'https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?w=600'

/** Safely coerce a value that may arrive as a JSON string into an array. */
function ensureArray<T>(value: T[] | string | null | undefined): T[] {
    if (Array.isArray(value)) return value
    if (typeof value === 'string') {
        try {
            const parsed = JSON.parse(value)
            if (Array.isArray(parsed)) return parsed
        } catch {
            /* ignore malformed JSON */
        }
    }
    return []
}

/**
 * Normalize API-returned sku data in place:
 *  - `related_services.button` may arrive as `{ text, url }`; downstream consumers expect `{ text, link }`.
 */
function normalizeSku(sku: Sku): void {
    const button = sku.related_services?.button as { link?: string; url?: string; text: string } | undefined
    if (button && !button.link && button.url) {
        button.link = button.url
    }
}

// insightsWidget is intentionally omitted — it's only added for lab SKUs via insertAfter below.
const DEFAULT_COMPONENT_ORDER: SkuComponentType[] = [
    'skuRefreshHeader',
    'skuVideoData',
    'skuClinicianWidget',
    'widgetContentAccordion',
    'skuContentAccordion',
    'skuStats',
    'widgetGenAi',
    'widgetTestimonialUgc',
    'skuSupportWidget',
    'skuBlogRoll',
    'widgetGetStartedCta',
    'relatedProducts',
    'widgetFaq',
]

/** Insert `toInsert` immediately after `anchor` in `order`. If anchor is missing, appends. No-op if `toInsert` is already present. */
function insertAfter(order: string[], toInsert: SkuComponentType, anchor: SkuComponentType): string[] {
    if (order.includes(toInsert)) return order
    const anchorIdx = order.indexOf(anchor)
    if (anchorIdx === -1) return [...order, toInsert]
    return [...order.slice(0, anchorIdx + 1), toInsert, ...order.slice(anchorIdx + 1)]
}

/** Move `item` to immediately after `anchor`, whether or not `item` is already in `order`. */
function repositionAfter(order: string[], item: SkuComponentType, anchor: SkuComponentType): string[] {
    const without = order.filter((component) => component !== item)
    const anchorIdx = without.indexOf(anchor)
    if (anchorIdx === -1) return [...without, item]
    return [...without.slice(0, anchorIdx + 1), item, ...without.slice(anchorIdx + 1)]
}

/** Move `item` to immediately before `anchor` in `order`. No-op if either is missing or `item` is already before `anchor`. */
function moveBefore(order: string[], item: SkuComponentType, anchor: SkuComponentType): string[] {
    const itemIdx = order.indexOf(item)
    const anchorIdx = order.indexOf(anchor)
    if (itemIdx === -1 || anchorIdx === -1 || itemIdx < anchorIdx) return order
    const without = order.filter((c) => c !== item)
    const insertAt = without.indexOf(anchor)
    return [...without.slice(0, insertAt), item, ...without.slice(insertAt)]
}

type ComponentBuilder = (content: SkuPageContent, sku: Sku) => SkuComponentConfig | null

const isLabSku = (sku: Sku) => sku.category === 'lab'

/** Anchor after which Trustpilot is inserted on refresh SKU pages. */
function productTrustpilotAnchor(order: string[], sku: Sku): SkuComponentType {
    if (isLabSku(sku)) {
        if (order.includes('insightsWidget')) return 'insightsWidget'
        if (order.includes('skuVideoData')) return 'skuVideoData'
        if (order.includes('widgetGenAi')) return 'widgetGenAi'
        return 'skuRefreshHeader'
    }
    if (order.includes('widgetGenAi')) return 'widgetGenAi'
    if (order.includes('skuClinicianWidget')) return 'skuClinicianWidget'
    if (order.includes('skuVideoData')) return 'skuVideoData'
    return 'skuRefreshHeader'
}

/** Visible text after stripping HTML tags and normalizing non-breaking spaces. */
function getVisibleTextFromHtml(html: string): string {
    let text = ''
    let insideTag = false
    for (const character of html) {
        if (character === '<') {
            insideTag = true
            continue
        }
        if (character === '>') {
            insideTag = false
            continue
        }
        if (!insideTag) {
            text += character
        }
    }
    return text.replace(/&nbsp;/gi, ' ').trim()
}

/**
 * Space out the slashes in compound words like `Fluticasone/Salmeterol` so a long
 * title has somewhere to wrap. Tokens with a letter-free part (`24/7`, dates, URLs)
 * keep their slashes tight.
 */
function addSpacesAroundSlashes(text: string): string {
    return text.replace(/\S*\/\S*/g, (token) => {
        const parts = token.split('/')
        return parts.every((part) => /[a-z]/i.test(part)) ? parts.join(' / ') : token
    })
}

/** True when rich-text HTML has no visible text (e.g. CMS placeholders like `<p></p>`). */
function isBlankRichText(value: string | undefined | null): boolean {
    if (!value?.trim()) return true
    return getVisibleTextFromHtml(value).length === 0
}

/** Flat icon-list accordions (e.g. wellness marketing) leave `content` empty; lab panels populate it. */
function isMarketingAccordion(content: SkuPageContent): boolean {
    const items = ensureArray<{ content?: string }>(content.accordion_items)
    return Boolean(content.accordion_title && items.length > 0 && items.every((item) => isBlankRichText(item.content)))
}

/**
 * Builds the "what is" video/text/feature-grid block config from a merged
 * `SkuPageContent`, or `null` when there's no `what_is_title` to show (e.g. no
 * content has been configured yet for this SKU/scope). Exported so callers outside
 * the SKU-page transform pipeline (e.g. the Product Detail Page) can render this
 * one block without going through the full `component_order` pipeline.
 */
export function buildSkuVideoDataConfig(content: SkuPageContent): SkuComponentConfigMap['skuVideoData'] | null {
    if (!content.what_is_title) return null
    return {
        type: 'skuVideoData',
        title: addSpacesAroundSlashes(content.what_is_title),
        content: content.what_is_description ?? '',
        mediaOverlayTitle: content.what_is_media_overlay_title ?? '',
        mediaOverlaySubtitle: content.what_is_media_overlay_subtitle ?? undefined,
        backgroundVideoSrc: content.what_is_video_src ?? undefined,
        modalVideoUrl: content.what_is_modal_video_url ?? undefined,
        mediaImageSrc: content.what_is_image_src ?? undefined,
        mediaImageAlt: content.what_is_image_alt ?? undefined,
        useCardsAsContent: content.what_is_format === 'numbered_rows',
        items: ensureArray(content.what_is_items).map((item) => ({
            title: item.title,
            description: item.description,
            icon: item.icon as any,
        })),
    }
}

const builders: Record<string, ComponentBuilder> = {
    skuRefreshHeader: (c, sku) => {
        const bulletPoints = ensureArray<string>(c.hero_bullet_points)
        if (!c.hero_title && !c.hero_description && !bulletPoints.length) {
            if (!isLabSku(sku)) return null
            return {
                type: 'skuRefreshHeader',
                title: addSpacesAroundSlashes(sku.name),
            }
        }
        const greenSectionItems = ensureArray<{ label: string; icon?: string }>(c.green_section_items)
        return {
            type: 'skuRefreshHeader',
            // CMS hero_title overrides the catalog SKU name; fall back to the name when unset.
            title: addSpacesAroundSlashes(c.hero_title || sku.name),
            description: c.hero_description ?? undefined,
            bulletPoints: bulletPoints.length ? bulletPoints : undefined,
            imageSrc: c.hero_image_url || undefined,
            imageAlt: c.hero_image_alt || undefined,
            greenSectionItems: greenSectionItems.length
                ? greenSectionItems.map((item) => ({ label: item.label, icon: item.icon as any }))
                : undefined,
        }
    },

    skuVideoData: (c) => buildSkuVideoDataConfig(c),

    skuClinicianWidget: (c) => {
        if (!c.clinician_title) return null
        return {
            type: 'skuClinicianWidget',
            title: c.clinician_title,
            bodyHtml: c.clinician_body_html ?? '',
            imageSrc: c.clinician_image_url ?? PLACEHOLDER_IMG,
            imageAlt: c.clinician_image_alt ?? 'Clinician',
        }
    },

    skuContentAccordion: (c, sku) => {
        // For labs, always emit the component so SkuRefreshRenderer can fall back
        // to buildLabAccordionConfig(sku.lab_details) when CMS data is absent.
        if (!c.accordion_title) {
            if (isLabSku(sku) && sku.lab_details?.test_details?.length) {
                return { type: 'skuContentAccordion', title: '', items: [] }
            }
            return null
        }
        return {
            type: 'skuContentAccordion',
            title: c.accordion_title,
            description: c.accordion_description ?? undefined,
            items: ensureArray(c.accordion_items).map((item) => ({
                title: item.title,
                content: item.content,
                icon: item.icon as any,
            })),
            infoGrid: ensureArray(c.accordion_info_grid).length ? ensureArray(c.accordion_info_grid) : undefined,
            showItemIcons: c.accordion_show_item_icons ?? undefined,
            defaultItemIcon: c.accordion_default_item_icon as any,
            showAllContent: c.accordion_show_all_content ?? undefined,
            showGetStartedButton: c.accordion_show_get_started ?? undefined,
            getStartedButtonLabel: c.accordion_get_started_label ?? undefined,
            leftImage: c.accordion_left_image_from_sku
                ? { fromSku: true }
                : c.accordion_left_image_url
                  ? { src: c.accordion_left_image_url, alt: c.accordion_left_image_alt ?? undefined }
                  : undefined,
        }
    },

    skuStats: (c) => {
        if (!c.stat_number && !c.stat_large_text) return null
        return {
            type: 'skuStats',
            megaStat: c.stat_number ?? undefined,
            imageAlt: c.stat_image_alt ?? undefined,
            description: c.stat_large_text ?? '',
            stats: [c.stat_column_1, c.stat_column_2].filter(Boolean) as string[],
        }
    },

    skuBlogRoll: (c) => {
        if (!c.blog_title || !c.blog_post) return null
        return {
            type: 'skuBlogRoll',
            title: c.blog_title,
            description: c.blog_description ?? '',
            eyebrow: c.blog_eyebrow ?? undefined,
            ctaLabel: c.blog_cta_label ?? undefined,
            ctaHref: c.blog_cta_href ?? undefined,
            post: c.blog_post,
        }
    },

    skuSupportWidget: (c) => {
        if (!c.reasons_title) return null
        const reasonsItems = ensureArray(c.reasons_items)
        if (!reasonsItems.length) return null
        return {
            type: 'skuSupportWidget',
            title: c.reasons_title,
            bodyHtml: c.reasons_description ?? '',
            cards: reasonsItems.map((item) => ({
                title: item.title,
                description: item.description,
                href: item.href,
                imageSrc: item.imageSrc ?? PLACEHOLDER_IMG,
                imageAlt: item.imageAlt ?? item.title,
            })),
        }
    },

    insightsWidget: (c, sku) => {
        // insights_items has an optional description in CMS; normalize to the
        // component's required { title, description } step shape.
        const steps = ensureArray<{ title: string; description?: string }>(c.insights_items).map((item) => ({
            title: item.title,
            description: item.description ?? '',
        }))
        // Require steps: a title alone is not a usable grid and must not replace the
        // component's built-in default (e.g. the four-step copy on lab pages).
        if (steps.length) {
            return { type: 'insightsWidget', data: { title: c.insights_title ?? '', steps } }
        }
        // No CMS steps: labs keep the component's built-in default; other SKUs skip it.
        return isLabSku(sku) ? { type: 'insightsWidget' } : null
    },

    skuLabDetailsAccordion: (c, sku) => {
        if (!isLabSku(sku) || !sku.lab_details?.test_details?.length) return null
        if (!isMarketingAccordion(c)) return null
        return { type: 'skuLabDetailsAccordion' }
    },

    widgetContentAccordion: (c) => {
        if (!c.widget_accordion_title) return null
        return {
            type: 'widgetContentAccordion',
            data: {
                title: c.widget_accordion_title,
                subtitle: c.widget_accordion_subtitle ?? '',
                images: (ensureArray(c.widget_accordion_images).length
                    ? ensureArray(c.widget_accordion_images)
                    : [PLACEHOLDER_IMG, PLACEHOLDER_IMG]) as any,
                accordionItems: ensureArray(c.widget_accordion_items).map((item) => ({
                    title: item.title,
                    description: item.description,
                })),
            },
            categoryName: c.widget_accordion_category_name ?? undefined,
        }
    },

    widgetFaq: (c) => {
        const faqItems = ensureArray(c.faq_items)
        if (!faqItems.length) return null
        return {
            type: 'widgetFaq',
            data: {
                title: c.faq_title ?? 'Frequently asked questions',
                items: faqItems,
            },
            categoryName: c.faq_category_name ?? undefined,
        }
    },

    widgetGenAi: (c) => {
        if (!c.gen_ai_title) return null
        return {
            type: 'widgetGenAi',
            data: {
                title: c.gen_ai_title,
                body: c.gen_ai_description ?? '',
                bookVisitOnly: c.gen_ai_book_visit_only ?? undefined,
            },
            categoryName: c.gen_ai_category_name ?? undefined,
        }
    },

    widgetTestimonialUgc: (c) => {
        const testimonialCards = ensureArray(c.testimonial_cards)
        if (!testimonialCards.length) return null
        return {
            type: 'widgetTestimonialUgc',
            data: {
                header: c.testimonial_header_title
                    ? {
                          title: c.testimonial_header_title,
                          description: c.testimonial_header_description ?? '',
                      }
                    : undefined,
                cards: testimonialCards.map((card) => {
                    const type = (card.type as 'quote' | 'image' | 'video') ?? 'quote'
                    if (type === 'image') {
                        return {
                            type: 'image' as const,
                            quote: card.quote,
                            name: card.name,
                            place: card.place,
                            image: card.image ?? PLACEHOLDER_IMG,
                            imageAlt: card.imageAlt ?? card.name,
                        }
                    }
                    if (type === 'video') {
                        return {
                            type: 'video' as const,
                            quote: card.quote,
                            name: card.name,
                            place: card.place,
                            videoUrl: card.videoUrl ?? '',
                            placeholderVideoSrc: card.placeholderVideoSrc,
                        }
                    }
                    return {
                        type: 'quote' as const,
                        quote: card.quote,
                        name: card.name,
                    }
                }),
            },
            categoryName: c.testimonial_category_name ?? undefined,
        }
    },

    widgetGetStartedCta: (c) => {
        if (!c.cta_heading) return null
        return {
            type: 'widgetGetStartedCta',
            data: {
                heading: c.cta_heading,
                body: c.cta_body_html ?? undefined,
                primaryCta: {
                    label: c.cta_label ?? 'Get started',
                    href: c.cta_href ?? undefined,
                },
            },
            categoryName: c.cta_category_name ?? undefined,
        }
    },

    relatedProducts: (content) => {
        const category = content.related_products_category
        return category ? { type: 'relatedProducts', category } : { type: 'relatedProducts' }
    },

    skuTrustpilotReviews: (_content, sku) => ({
        type: 'skuTrustpilotReviews',
        variant: getSkuPageTrustpilotVariant(sku.category) ?? 'product',
    }),
}

export function transformSkuPageContent(sku: Sku): SkuRefreshPageConfig {
    normalizeSku(sku)
    const content = sku.page_content
    if (!content) return { components: [] }

    const orderFromContent = ensureArray<string>(content.component_order)
    let order = orderFromContent.length ? orderFromContent : DEFAULT_COMPONENT_ORDER

    // Lab pages rely on sku.lab_details-driven fallbacks in SkuRefreshRenderer,
    // so force these components into the order even if CMS didn't list them.
    if (isLabSku(sku)) {
        const marketingAccordion = isMarketingAccordion(content)
        order = insertAfter(order, 'insightsWidget', 'skuRefreshHeader')

        if (marketingAccordion) {
            order = insertAfter(order, 'skuContentAccordion', 'skuRefreshHeader')
        }

        const detailAccordionType: SkuComponentType = marketingAccordion
            ? 'skuLabDetailsAccordion'
            : 'skuContentAccordion'

        // Gray video / what-is block stays above insights (original lab position).
        if (order.includes('skuVideoData')) {
            const videoAnchor: SkuComponentType = marketingAccordion ? 'skuContentAccordion' : 'skuRefreshHeader'
            if (order.includes(videoAnchor)) {
                order = repositionAfter(order, 'skuVideoData', videoAnchor)
            }
            if (order.includes('insightsWidget')) {
                order = moveBefore(order, 'skuVideoData', 'insightsWidget')
            }
        }

        // Blue GM banner sits after insights, before the lab-details accordion.
        if (order.includes('widgetContentAccordion') && order.includes('insightsWidget')) {
            order = repositionAfter(order, 'widgetContentAccordion', 'insightsWidget')
        }

        if (order.includes(detailAccordionType)) {
            const detailAnchor: SkuComponentType = order.includes('widgetContentAccordion')
                ? 'widgetContentAccordion'
                : 'insightsWidget'
            order = repositionAfter(order, detailAccordionType, detailAnchor)
        } else {
            const detailAnchor: SkuComponentType = order.includes('widgetContentAccordion')
                ? 'widgetContentAccordion'
                : 'insightsWidget'
            order = insertAfter(order, detailAccordionType, detailAnchor)
        }

        order = moveBefore(order, 'widgetContentAccordion', 'skuClinicianWidget')
        order = moveBefore(order, detailAccordionType, 'skuClinicianWidget')
    }

    if (showsProductPageTrustpilot(sku.category)) {
        order = order.filter((type) => type !== 'widgetTestimonialUgc')
        order = insertAfter(order, 'skuTrustpilotReviews', productTrustpilotAnchor(order, sku))
    }

    const components: SkuComponentConfig[] = []
    for (const type of order) {
        const build = builders[type]
        if (!build) continue
        const config = build(content, sku)
        if (config) components.push(config)
    }

    return { components }
}
